import { selfServiceSupabase } from './selfServiceSupabase';

/**
 * Centralized fetch wrapper for calls to the selfservice-api NestJS service.
 *
 * Replaces the legacy direct calls to Supabase edge functions (selfservice-*).
 * Adds the current Supabase session JWT to every request as `Authorization:
 * Bearer <token>`, when one is available. Works for both anonymous (no token)
 * and signed-in (token present) flows; the API gates each route via @Public,
 * @MaybeAuth, or @Roles decorators.
 *
 * Base URL resolution order:
 *   1. VITE_SELFSERVICE_API_URL (set per-env in .env.local)
 *   2. http://localhost:3100 in dev
 *   3. (otherwise throw — explicit failure beats a silent same-origin POST)
 */

const API_BASE_URL = (() => {
  const explicit = import.meta.env.VITE_SELFSERVICE_API_URL as string | undefined;
  if (explicit) return explicit.replace(/\/+$/, '');
  if (import.meta.env.DEV) return 'http://localhost:3100';
  throw new Error(
    'VITE_SELFSERVICE_API_URL is not set. The frontend cannot reach the self-service API.',
  );
})();

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOptions {
  /** When true, do not attach an Authorization header even if a session exists. */
  anonymous?: boolean;
  /** Override fetch signal for cancellation. */
  signal?: AbortSignal;
  /** Extra headers to merge in. */
  headers?: Record<string, string>;
}

async function authHeader(): Promise<Record<string, string>> {
  if (!selfServiceSupabase) return {};
  try {
    const { data } = await selfServiceSupabase.auth.getSession();
    const token = data.session?.access_token;
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

async function request<T>(
  method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  path: string,
  body?: unknown,
  opts: RequestOptions = {},
): Promise<T> {
  const url = `${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    ...(opts.headers ?? {}),
  };
  if (!opts.anonymous) {
    Object.assign(headers, await authHeader());
  }

  const res = await fetch(url, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal: opts.signal,
  });

  const text = await res.text();
  let parsed: unknown = text;
  if (text && (res.headers.get('content-type') ?? '').includes('application/json')) {
    try {
      parsed = JSON.parse(text);
    } catch {
      /* keep raw text */
    }
  }

  if (!res.ok) {
    const fromObject =
      parsed && typeof parsed === 'object' && 'message' in parsed
        ? (parsed as { message: unknown }).message
        : null;
    // class-validator returns an array of strings for ValidationPipe errors —
    // joining them surfaces the actual field that failed rather than a
    // generic "400 Bad Request" that hides the cause.
    const messageFromArray = Array.isArray(fromObject)
      ? fromObject.filter((s): s is string => typeof s === 'string').join('; ')
      : null;
    const message =
      messageFromArray ||
      (typeof fromObject === 'string' ? fromObject : null) ||
      (typeof parsed === 'string' ? parsed : null) ||
      `${res.status} ${res.statusText}`;
    throw new ApiError(message, res.status, parsed);
  }

  return parsed as T;
}

export const apiClient = {
  get: <T>(path: string, opts?: RequestOptions) => request<T>('GET', path, undefined, opts),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>('POST', path, body, opts),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>('PATCH', path, body, opts),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>('PUT', path, body, opts),
  delete: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>('DELETE', path, body, opts),
};

export const apiBaseUrl = API_BASE_URL;

// A `legacyInvoke()` compat helper used to live here for the in-flight
// migration from Supabase edge functions to NestJS. Every call site is now
// using `apiClient` directly, so the helper and the edge-function name →
// route map have been removed. If you find a remaining call site that
// references the legacy `selfservice-*` names, migrate it to an explicit
// `apiClient.post` call rather than re-introducing the indirection.
